import React, {
    useCallback, useState, useContext, useEffect
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import {
    ModalNextProvider
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { messages as commonMessages } from 'gw-platform-translations';
import { claimsMessages } from 'gw-capability-claim-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import messages from '../../FNOL.messages';
import metadata from './CustomizedUploadComponent.metadata.json5';
import styles from './CustomizedUploadComponent.module.scss';

function CustomizedUploadComponent(props) {
    const {
        path: claimVM,
        updatePath: updateWizardData,
        authHeader,
        proofOfPaymentRequired,
        addInsuredButtonClicked: addInsuredBankButtonClicked,
        isReimbursementSelected: isReimbursementSelected,
        isPaymentIconClicked: paymentIconClicked,
        setPaymentIconClicked
    } = props;

    const { ClaimService } = useDependencies('ClaimService');
    const { ClaimDownloadService } = useDependencies('ClaimDownloadService');
    const [selectedIcon, setSelectedIcon] = useState(-1);
    const [showLoader, setShowLoader] = useState(false);
    const translator = useContext(TranslatorContext);

    const iconMap = {
        Claim_Form: 'mi-description',
        // Bank_Settlement_letter: '',
        // Registration_Certificate: '',
        Coupon_Covering: 'mi-local-play',
        Policy_Schedule_Covering: 'mi-policy',
        Statement: 'mi-assignment-ind',
        Proof_Of_Payment: 'mi-money',
        Assessor_Report: 'mi-receipt',
        Fleet_List_Declaration_Endorsement: 'mi-local-shipping',
        Material_Damage: 'mi-assignment-late',
        Loss_Adjusters_report: 'mi-assignment',

        Photos: 'mi-add-a-photo',
        Policy_Schedule: 'mi-policy',
        Proof_of_Pemium: 'mi-payment',
        Merit_Statement: 'mi-assignment',
        Quantum_Docs: 'mi-receipt',
        Sasria_Coupon: 'mi-local-play',
        Banking_Details: 'mi-account-balance',
        Repair_Invoice: 'mi-build',
        Agreement_of_Loss: 'mi-assignment-turned-in',
        Other: 'mi-attach-file'

    };

    const fileMimeTypeMap = {
        csv: 'application/csv',
        doc: 'application/msword',
        msg: 'application/vnd.ms-outlook',
        eml: 'message/rfc822',
        zip: 'application/x-zip-compressed',
        pdf: 'application/pdf',
        ps: 'application/postscript',
        rtf: 'application/rtf',
        xls: 'application/vnd.ms-excel',
        ppt: 'application/vnd.ms-powerpoint',
        pps: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        wav: 'audio/wav',
        wma: 'audio/x-ms-wma',
        bmp: 'image/bmp',
        gif: 'image/gif',
        jpg: 'image/jpeg',
        tiff: 'image/tiff',
        tif: 'image/tiff',
        mdi: 'image/vnd.ms-modi',
        png: 'image/png',
        html: 'text/html',
        htm: 'text/html',
        txt: 'text/plain',
        rtx: 'text/richtext',
        xml: 'text/xml',
        mpg: 'video/mpeg',
        mpeg: 'video/mpeg',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        mp4: 'video/mp4'
    };


    const getFileExtension = useCallback((name) => {
        const extension = name.split('.').pop();
        return extension || '';
    }, []);

    const getMimeType = useCallback((name) => {
        const fileExtension = getFileExtension(name);
        let mimeType = _.get(fileMimeTypeMap, fileExtension);
        if (_.isNil(mimeType)) {
            mimeType = 'application/octet-stream';
        }
        return mimeType;
    }, [fileMimeTypeMap, getFileExtension]);

    const setItemVisibility = useCallback(
        (item) => {
            if (_.isNil(item)) {
                return false;
            }
            if (selectedIcon === -1) {
                return true;
            }
            let iconListToShow = [];
            const documentTypes = _.get(claimVM, 'bankDocumentCheckListSTP_EXT.value', []);
            if (isReimbursementSelected === 'No') {
                iconListToShow = documentTypes.filter((doc) => doc.sasriaForm !== 'Proof_Of_Payment' && doc.sasriaForm !== 'Repair_Invoice');
                //_.set(claimVM.value, 'bankDocumentCheckListSTP_EXT', iconListToShow);
            }
            else {
                iconListToShow = documentTypes;
            }
            return item.description === (!_.isEmpty(iconListToShow) && iconListToShow.length === 1 ? iconListToShow[0].sasriaForm : iconListToShow[selectedIcon].sasriaForm);
        },
        [claimVM, isReimbursementSelected, selectedIcon]
    );

    const getFileIcon = useCallback((extension) => {
        switch (extension) {
            case 'doc':
            case 'docx':
            case 'txt':
            case 'docm':
                return 'file-word';
            case 'xlt':
            case 'xlsx':
            case 'xlsm':
            case 'xls':
                return 'file-excel';
            case 'gif':
            case 'png':
            case 'jpeg':
                return 'file-image';
            case 'pdf':
                return 'file-pdf';
            default:
                return 'file';
        }
    }, []);

    const changeUploadDocmentImageType = useCallback(
        (documentUploadItem) => {
            const extension = getFileExtension(documentUploadItem.docUID).toLowerCase() || '';
            return `${getFileIcon(extension)}`;
        },
        [getFileExtension, getFileIcon]
    );

    const handleDocumentDownload = useCallback(
        (item) => {
            if (_.isNil(item)) {
                return null;
            }
            const { workingPublicID, sessionID } = item;
            return ClaimDownloadService
                .getClaimDocument(workingPublicID, sessionID);
        },
        [ClaimDownloadService]
    );

    const handleError = useCallback(
        (title, message) => {
            return ModalNextProvider.showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    const onDeleteDocumentIcon = useCallback(
        (e, item) => {
            e.preventDefault();
            const { claimNumber, publicID, name } = item;
            ModalNextProvider.showConfirm({
                title: claimsMessages.confirmationToRemoveTitle,
                message: translator(messages.fnolRemoveUploadedDocument, { documentName: name }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: claimsMessages.removeConfirmationYes,
                cancelButtonText: claimsMessages.removeConfirmationNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setShowLoader(true);
                try {
                    const isDeleteItem = await ClaimService.claimsRemoveDocument(
                        [claimNumber, publicID],
                        authHeader
                    );
                    if (isDeleteItem === false) {
                        setShowLoader(false);
                        return handleError(
                            claimsMessages.deletionFailedTitle,
                            claimsMessages.deletionFailedMessage
                        );
                    }
                    const documentsArray = _.get(claimVM, 'documents.value');
                    const updatedDocArray = documentsArray.filter((documentObj) => {
                        return documentObj.publicID !== publicID;
                    });
                    const newClaimVM = _.clone(claimVM);
                    _.set(newClaimVM, 'documents.value', updatedDocArray);
                    updateWizardData(newClaimVM);
                    setShowLoader(false);
                    return true;
                } catch (documentDeletionError) {
                    setShowLoader(false);
                    return handleError(
                        claimsMessages.removeServiceFailedTitle,
                        claimsMessages.removeServiceFailedMessage
                    );
                }
            }, _.noop);
        },
        [translator, ClaimService, authHeader, claimVM, updateWizardData, handleError]
    );

    const showDocumentDeleteIcon = useCallback((documentUploadItem) => {
        if (documentUploadItem.canDelete) {
            return true;
        }
        return false;
    }, []);

    const generateMoreInfoDocumentsOverrides = useCallback(() => {
        const documentUploadPath = 'value.documents';
        const documentUploadItems = _.get(claimVM, documentUploadPath, []);
        const bankIconDocs = documentUploadItems.filter((doc) => (doc.sasriaFormName_EXT === 'Proof Of Payment'
            || doc.sasriaFormName_EXT === 'Agreement of Loss' || doc.sasriaFormName_EXT === 'Repair Invoice'));
        const overrides = bankIconDocs.map((documentUploadItem, index) => {
            return {
                [`uploadDocumentItem${index}`]: {
                    visible: setItemVisibility(documentUploadItem)
                },
                [`documentLeftSection${index}`]: {
                    icon: changeUploadDocmentImageType(documentUploadItem)
                },
                [`documentNameLink${index}`]: {
                    href: handleDocumentDownload(documentUploadItem)
                },
                [`documentDeleteSection${index}`]: {
                    onClick: (e) => {
                        onDeleteDocumentIcon(e, documentUploadItem);
                    }
                },
                [`documentDeleteIcon${index}`]: {
                    visible: showDocumentDeleteIcon(documentUploadItem)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [changeUploadDocmentImageType, claimVM, handleDocumentDownload,
        onDeleteDocumentIcon, setItemVisibility, showDocumentDeleteIcon]);

    const generateDocumentsOverrides = useCallback(() => {
        const documentUploadPath = 'value.documents';
        let docList = [];
        const documentUploadItems = _.get(claimVM, documentUploadPath, []);
        if (!_.isEmpty(documentUploadItems) && addInsuredBankButtonClicked === true) {
            docList = documentUploadItems.filter((doc) => doc.sasriaFormName_EXT === 'Account or Bank Confirmation' && doc.isDocumentTaggedToInsuredBankSTP_EXT === true);
        }
        else if (!_.isEmpty(documentUploadItems) && addInsuredBankButtonClicked !== true) {
            docList = documentUploadItems.filter((doc) => (doc.sasriaFormName_EXT === 'Proof Of Payment'
                || doc.sasriaFormName_EXT === 'Agreement of Loss' || doc.sasriaFormName_EXT === 'Repair Invoice'));
        }
        const overrides = docList.map((documentUploadItem, index) => {
            return {
                [`documentName${index}`]: {
                    value: documentUploadItem.name
                },
                [`docStatus${index}`]: {
                    value: documentUploadItem.sasriaDocumentStatus_EXT
                },
                [`dateModified${index}`]: {
                    value: documentUploadItem.sasriaDocModifiedDate_EXT
                },
                [`sasriaFormName${index}`]: {
                    value: documentUploadItem.sasriaFormName_EXT
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [addInsuredBankButtonClicked, claimVM]);


    const getDescription = useCallback(
        (index) => {
            if (index === -1) {
                return null;
            }
            let iconListToShow = [];
            const documentTypes = _.get(claimVM, 'bankDocumentCheckListSTP_EXT.value', []);
            if (isReimbursementSelected === 'No') {
                iconListToShow = documentTypes.filter((doc) => doc.sasriaForm !== 'Proof_Of_Payment' && doc.sasriaForm !== 'Repair_Invoice');
                //_.set(claimVM.value, 'bankDocumentCheckListSTP_EXT', iconListToShow);
            }
            else {
                iconListToShow = documentTypes;
            }
            return _.isEmpty(iconListToShow) && iconListToShow.length === 1 ? iconListToShow[0].sasriaForm : iconListToShow[index].sasriaForm;
        },
        [claimVM, isReimbursementSelected]
    );

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await ClaimService.claimsDocUploadToken([], authHeader);
            return uploadTokenID;
        } catch (e) {
            return handleError(
                commonMessages.errorUploadTitle,
                commonMessages.errorGenerateUploadToken
            );
        }
    }, [ClaimService, authHeader, handleError]);

    const onUploadDocument = useCallback(
        async (file) => {
            setShowLoader(true);
            const documentMetaDataTemplate = {
                DocUID: '001',
                DocumentType: 'fnol',
                SecurityType: 'unrestricted',
                Status: 'approved',
                Author: 'Policy Holder',
                // claimNumber: claimVM.claimNumber.value F2\21\999993,
                description: (addInsuredBankButtonClicked === false) ? getDescription(selectedIcon) : 'Banking_Details',
                sasriaDocumentForm_EXT: (addInsuredBankButtonClicked === false) ? getDescription(selectedIcon) : 'Banking_Details',
                claimNumber: claimVM.claimNumber.value,
                name: file.name,
                isSTPForPE_Ext: true,
                isDocumentTaggedToInsuredBankSTP_EXT: addInsuredBankButtonClicked === true,
                // mimeType: file.type,
                mimeType: getMimeType(file.name),
                sessionID: await claimsDocUploadToken()
            };
            try {
                const data = await ClaimService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                const documentsArray = _.get(claimVM, 'documents.value') || [];
                documentsArray.push(data);
                const newClaimVM = _.clone(claimVM);
                _.set(newClaimVM, 'documents.value', documentsArray);
                updateWizardData(newClaimVM);
                setShowLoader(false);
            } catch (error) {
                setShowLoader(false);
                handleError(commonMessages.errorUploadTitle, commonMessages.uploadFailedMessage);
            }
        },
        [addInsuredBankButtonClicked, getDescription, selectedIcon, claimVM, getMimeType, claimsDocUploadToken, ClaimService, authHeader, updateWizardData, handleError]
    );

    const onIconClicked = useCallback(
        (evt, index) => {
            if (index === selectedIcon) {
                setSelectedIcon(-1);
            } else {
                setSelectedIcon(index);
            }
            let iconListToShow = [];
            const documentTypes = _.get(claimVM, 'bankDocumentCheckListSTP_EXT.value', []);
            if (isReimbursementSelected === 'No') {
                iconListToShow = documentTypes.filter((doc) => doc.sasriaForm !== 'Proof_Of_Payment' && doc.sasriaForm !== 'Repair_Invoice');
                //_.set(claimVM.value, 'bankDocumentCheckListSTP_EXT', iconListToShow);
            }
            else {
                iconListToShow = documentTypes;
            }
            const docType = _.isEmpty(iconListToShow) && iconListToShow.length === 1 ? iconListToShow[0].sasriaForm : iconListToShow[index].sasriaForm;
            if (docType === 'Proof_Of_Payment') {
                setPaymentIconClicked(true);
            }
            if (docType !== 'Proof_Of_Payment') {
                setPaymentIconClicked(false);
            }
        },
        [claimVM, isReimbursementSelected, selectedIcon, setPaymentIconClicked]
    );


    const getIconClass = useCallback(
        (index) => {
            if (index === selectedIcon) {
                return 'selected-icon';
            }
            return '';
        },
        [selectedIcon]
    );

    const getDocTypeIcon = useCallback(
        (docType) => {
            const iconName = _.get(iconMap, docType.sasriaForm);
            if (!_.isNil(iconName)) {
                return iconName;
            }
            return 'mi-attach-file';
        },
        [iconMap]
    );

    const generateIconOverrides = useCallback(() => {
        const documentTypes = _.get(claimVM, 'bankDocumentCheckListSTP_EXT.value', []);
        let iconListToShow = [];
        if (isReimbursementSelected === 'No') {
            iconListToShow = documentTypes.filter((doc) => doc.sasriaForm !== 'Proof_Of_Payment' && doc.sasriaForm !== 'Repair_Invoice');
            //_.set(claimVM.value, 'bankDocumentCheckListSTP_EXT', iconListToShow);
        }
        else {
            iconListToShow = documentTypes;
            //_.set(claimVM.value, 'bankDocumentCheckListSTP_EXT', unalteredIconList);
        }
        const overrides = iconListToShow.map((docType, index) => {
            return {
                [`documentIcon${index}`]: {
                    icon: getDocTypeIcon(docType),
                    onClick: (e) => {
                        onIconClicked(e, index);
                    },
                    tooltip: {
                        text: docType.sasriaFormName,
                        placement: 'bottom'
                    },
                    iconClassName: getIconClass(index)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [claimVM, getDocTypeIcon, getIconClass, isReimbursementSelected, onIconClicked]);


    const getDocHeaderOnIconSelection = useCallback(
        (index) => {
            if (index === -1) {
                return null;
            }
            let iconListToShow = [];
            const documentTypes = _.get(claimVM, 'bankDocumentCheckListSTP_EXT.value', []);
            if (isReimbursementSelected === 'No') {
                iconListToShow = documentTypes.filter((doc) => doc.sasriaForm !== 'Proof_Of_Payment' && doc.sasriaForm !== 'Repair_Invoice');
                //_.set(claimVM.value, 'bankDocumentCheckListSTP_EXT', iconListToShow);
            }
            else {
                iconListToShow = documentTypes;
            }
            return !_.isEmpty(iconListToShow) && iconListToShow.length === 1 ? iconListToShow[0].sasriaFormName : claimVM.value.bankDocumentCheckListSTP_EXT[index].sasriaFormName;
        },
        [claimVM, isReimbursementSelected]
    );

    const bankDocumentList = useCallback(
        () => {
            let bankDocuments = [];
            const documentList = _.get(claimVM, 'documents.value', []);
            if (!_.isEmpty(documentList) && addInsuredBankButtonClicked === true) {
                bankDocuments = documentList.filter((doc) => doc.sasriaFormName_EXT === 'Account or Bank Confirmation' && doc.isDocumentTaggedToInsuredBankSTP_EXT === true);
            } else if (!_.isEmpty(documentList) && addInsuredBankButtonClicked !== true) {
                bankDocuments = documentList.filter((doc) => (doc.sasriaFormName_EXT === 'Proof Of Payment'
                    || doc.sasriaFormName_EXT === 'Agreement of Loss' || doc.sasriaFormName_EXT === 'Repair Invoice'));
            }

            return bankDocuments;
        },
        [addInsuredBankButtonClicked, claimVM],
    );

    const loadIcons = useCallback(
        () => {
            let iconListToShow = [];
            const iconsToRenderForbank = _.get(claimVM, 'bankDocumentCheckListSTP_EXT.value', []);
            if (isReimbursementSelected === 'No') {
                iconListToShow = iconsToRenderForbank.filter((doc) => doc.sasriaForm !== 'Proof_Of_Payment' && doc.sasriaForm !== 'Repair_Invoice');
            }
            else{
                iconListToShow = iconsToRenderForbank;
            }
            return iconListToShow;
        },
        [claimVM, isReimbursementSelected],
    );

    const overrides = {
        addtionalInformationLoader: {
            loaded: !showLoader
        },
        uploadDocumentGrid: {
            visible: !showLoader
        },

        iconIterable: {
            value: loadIcons()
        },

        iconContainer: {
            visible: addInsuredBankButtonClicked !== true
        },
        uploadDocument: {
            visible: addInsuredBankButtonClicked === true ? true : addInsuredBankButtonClicked !== true && selectedIcon !== -1
        },
        // uploadDocument: {
        //     visible: selectedIcon !== -1
        // },
        addMoreInfoLabel: {
            visible: selectedIcon === -1
        },

        uploadDocumentItems: {
            value: bankDocumentList()
        },
        selectedIconDocName: {
            visible: selectedIcon !== -1,
            content: getDocHeaderOnIconSelection(selectedIcon)
        },
        ...generateMoreInfoDocumentsOverrides(),
        ...generateDocumentsOverrides(),
        ...generateIconOverrides()
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onUploadDocument: onUploadDocument,
            onIconClicked: onIconClicked
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={claimVM}
            overrideProps={overrides}
            onModelChange={updateWizardData}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

CustomizedUploadComponent.propTypes = {
    path: PropTypes.shape({}).isRequired,
    authHeader: PropTypes.shape({}).isRequired,
    updatePath: PropTypes.func.isRequired,
};
export default withAuthenticationContext(CustomizedUploadComponent);
